'use strict';
/* Karmagick v1.0.4 — la luna fatta di stelle: la sezione della pagina iniziale subito dopo l'apertura
   (sections/kg-home-luna.liquid) e la pagina della luna (templates/page.luna.json).
   Lo stesso cielo dell'apertura (assets/kg-altare.js): stesse stelle agganciate ai pixel con la loro fase
   fissa, stessi colori, stessa nebbia, stesso volo dalla galassia alla forma, stesso testo scritto dalle
   stelle che poi si accende vero. Le forme vengono dal cielo vero (assets/kg-cielo.js):
   - il disco della luna, coi mari e la luce cinerea: la fase la calcola lo shader da un angolo, così la
     luna può cambiare fase sotto gli occhi (all'arrivo il mese «ripassa» dalla luna nuova a stanotte;
     nella pagina si scorrono i giorni). Dal 2026-10-30 occupa tutto il suo posto: l'utente ha tolto
     l'anello del mese che le girava attorno («il "cerchio" intorno alla luna toglilo, e ingrandisci la luna»);
   - la ruota dell'anno «La luce del giorno» (2026-10-29, scelta dall'utente fra tre proposte:
     reports/ruota-2026-10-29/), nella pagina della luna e, dal 2026-10-30, anche nella home al posto della
     piccola ruota a raggi: un anello largo quanto le ore di luce a 45° nord, sottile a Yule e pieno a Litha,
     con le stelle che scorrono nel verso dell'anno, le tacche delle feste e il sole ☉.
   Tutto si aggiorna da solo mentre la pagina resta aperta (l'orologio in fondo al file).
   Le forme stanno dove la pagina mette i loro posti ([data-kg-posto="luna" | "ruota"]), le lettere dove la
   pagina ha scritto il testo ([data-kg-scrivi]); il bordo dei pulsanti è [data-kg-bordo].
   Senza WebGL2 restano i disegni in SVG e il testo; con prefers-reduced-motion niente volo né ripasso. */
(() => {
  /* ogni sezione della pagina della luna porta i suoi script: il motore parte una volta sola */
  if (window.KG_LUNA) return;
  const CIELO = window.KG_CIELO;
  const radici = [...document.querySelectorAll('[data-kg-luna]')];
  if (!CIELO || !radici.length) return;

  const fermo = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const SCALA = 1.05, CAM = 3.2, FUORI = 9, VOLO = 2.6, RIPASSO = 3.6, VELO = 0.42;
  const gauss = () => { let u = 0, v = 0; while (!u) u = Math.random(); while (!v) v = Math.random(); return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v); };
  const dpr = () => Math.min(window.devicePixelRatio || 1, 1.75);
  const molla = (valore, rigidita, smorzamento) => ({
    v: valore, obiettivo: valore, vel: 0,
    passo(dt) { const f = (this.obiettivo - this.v) * rigidita - this.vel * smorzamento; this.vel += f * dt; this.v += this.vel * dt; return this.v; },
  });
  const liscia = (u) => (u < 0.5 ? 4 * u * u * u : 1 - Math.pow(-2 * u + 2, 3) / 2);
  const inChiaro = (v) => {
    if (typeof v === 'string') { const t = document.createElement('textarea'); t.innerHTML = v; return t.value; }
    if (Array.isArray(v)) return v.map(inChiaro);
    if (v && typeof v === 'object') for (const k of Object.keys(v)) v[k] = inChiaro(v[k]);
    return v;
  };

  /* ── gli shader: quelli dell'apertura, con le tre forme del cielo vero ── */
  const VSP = `#version 300 es
precision highp float;
in vec4 aGal; in vec4 aRnd; in vec3 aDa; in vec3 aA; in vec4 aMDa; in vec4 aMA; in vec4 aNDa; in vec4 aNA;
uniform float tempo, morph, passaggio, aspetto, dpr, scala, cam, forza, velo, giroCta, sopraCta, faseL, annoP, festaU, ruotaR;
uniform vec2 topo, centro, incl, ris;
uniform vec4 zone[4];
out vec3 vCol; out float vA;
mat2 rot(float a) { float c = cos(a), s = sin(a); return mat2(c, -s, s, c); }
float dentro(vec2 p, vec4 z, float m) {
  vec2 a = smoothstep(z.xy - m, z.xy + m * 0.35, p) * (1.0 - smoothstep(z.zw - m * 0.35, z.zw + m, p));
  return a.x * a.y;
}
/* il testo ha il bersaglio sullo schermo: qui si riporta nel cielo, a profondità zero */
vec3 posto(vec3 q, vec4 n) {
  if (n.x < 10.5 || n.x > 12.5) return q;
  vec2 w = (q.xy - centro) * vec2(aspetto, 1.0) / scala * cam + incl * vec2(0.9, -0.6);
  return vec3(w, 0.0);
}
vec3 colore(float t) {
  if (t < 0.5) return vec3(1.0, 0.95, 0.84);
  if (t < 1.5) return vec3(0.88, 0.92, 1.0);
  if (t < 2.5) return vec3(1.0, 0.8, 0.46);
  if (t < 3.5) return vec3(0.6, 0.66, 0.85);
  if (t < 5.5) return vec3(0.7, 0.72, 0.78);
  return vec3(0.96, 0.9, 0.78);
}
/* la ruota dell'anno: u da 0 (in alto, Yule) in senso orario. Le ore di luce a 45° nord per un punto della
   ruota, come KG_CIELO.oreDiLuceSullaRuota: declinazione del sole, alba e tramonto a −0°50′ */
vec2 sulGiro(float u, float r) { return vec2(sin(u * 6.2832), cos(u * 6.2832)) * r; }
float oreDiLuce(float u) {
  float l = radians(270.0 + 360.0 * u);
  float d = asin(0.397736 * sin(l));
  float c = (-0.014544 - 0.707107 * sin(d)) / (0.707107 * cos(d));
  return 2.0 * acos(clamp(c, -1.0, 1.0)) * 3.819719;
}
/* la luna viva, la ruota dell'anno; le lettere e il bordo dei pulsanti */
vec3 anima(vec3 p, vec4 n, inout float luce, inout float misura, inout float tinta) {
  float tipo = n.x, fase = n.y;
  if (tipo > 3.5 && tipo < 4.5) {
    /* le scintille, come nell'apertura: la scia sull'acqua e il bordo della luna nuova */
    float f = pow(max(0.0, sin(tempo * 1.1 + fase * 47.0)), 60.0);
    luce *= 1.0 + f * 5.0; misura *= 1.0 + f * 1.5;
    return p;
  }
  if (tipo < 10.5) return p;
  if (tipo < 11.5) {
    float f = pow(max(0.0, sin(tempo * 0.8 + fase * 41.0)), 30.0);
    luce *= 1.0 + f * 2.2; misura *= 1.0 + f * 0.6;
    return p;
  }
  if (tipo < 12.5) {
    float d = fract(giroCta - n.z);
    float cometa = exp(-d * 9.0);
    luce *= 0.5 + (1.2 + sopraCta) * cometa + sopraCta * 0.4;
    misura *= 1.0 + cometa * 0.5;
    return p;
  }
  if (tipo < 13.5) {
    /* il disco: (n.z, n.w) è il punto sul disco di raggio 1. La luce viene dal sole a un angolo faseL
       dalla luna nuova (0) alla piena (π): come nella luna dell'apertura, ma calcolata qui */
    vec2 s = vec2(sin(faseL), -cos(faseL));
    float d = dot(n.zw, n.zw);
    float l = n.z * s.x + sqrt(max(0.0, 1.0 - d)) * s.y;
    float pieno = clamp(l * 6.0, 0.0, 1.0);
    float w = smoothstep(-0.015, 0.025, l);
    /* fase = soglia della stella (già divisa per la densità dei mari): sul lato acceso ci sono più stelle
       dove la luce è piena, sul lato buio solo qualcuna, tenue: la luce cinerea */
    float acceso = (1.0 - smoothstep(0.3 + 0.7 * pieno - 0.04, 0.3 + 0.7 * pieno + 0.04, fase)) * (0.42 + 0.48 * pieno);
    float buio = (1.0 - smoothstep(0.05, 0.07, fase)) * 0.1;
    luce *= mix(buio, acceso, w);
    misura *= mix(0.9, 1.0, w);
    tinta = mix(3.0, tinta, step(0.5, w));
    return p;
  }
  if (tipo > 15.5) {
    /* la ruota dell'anno (16-19): p.xy è il centro della ruota; l'anello va dal 48% del raggio a tutto il
       raggio, che raggiunge a Litha (15 h 37′ di luce): lo spessore è proporzionale alle ore di luce */
    float P = fract(annoP), rin = ruotaR * 0.48, k = ruotaR * 0.52 / 15.62;
    if (tipo < 16.5) {
      /* 16, l'anello: le stelle scorrono nel verso dell'anno (un giro in 150 s) e si allargano con la luce;
         n.w è dove stanno fra il bordo di dentro (0) e quello di fuori (1). Acceso l'arco fino alla prossima festa */
      float u = fract(n.z + tempo / 150.0), D = oreDiLuce(u);
      float a = fract(festaU - P), manca = 1.0 - smoothstep(a - 0.004, a + 0.004, fract(u - P));
      luce *= mix(0.5, 1.0, smoothstep(8.0, 15.8, D)) * mix(0.5, 1.0, manca);
      p.xy += sulGiro(u, rin + n.w * k * D) + (aRnd.zw - 0.5) * ruotaR * 0.006;
      return p;
    }
    if (tipo < 17.5) {
      /* 17, le feste: una tacca di traverso all'anello, appena oltre i bordi; quella della prossima pulsa */
      float lei = 1.0 - smoothstep(0.004, 0.012, abs(fract(n.z - festaU + 0.5) - 0.5));
      float pulsa = 0.5 + 0.5 * sin(tempo * 1.7);
      luce *= mix(0.8, 1.25 + 0.45 * pulsa, lei);
      misura *= mix(1.0, 1.1 + 0.15 * pulsa, lei);
      p.xy += sulGiro(n.z, rin + n.w * k * oreDiLuce(n.z)) + sulGiro(n.z + 0.25, (aRnd.z - 0.5) * ruotaR * 0.012);
      return p;
    }
    float fuori = rin + k * oreDiLuce(P);
    if (tipo < 18.5) {
      /* 18, il sole dove sta oggi, sul bordo di fuori (fin dove arriva la luce di oggi): il suo segno ☉, un
         punto e un anello che gira piano */
      float r0 = ruotaR * 0.08;
      vec2 c = p.xy + sulGiro(P, fuori);
      if (n.w < 0.5) { luce *= 1.0 + 0.15 * sin(tempo * 1.3 + fase * 6.0); p.xy = c + (aRnd.zw - 0.5) * r0 * 0.4; }
      else {
        float g = n.z + tempo * 0.18;
        luce *= 0.8 + 0.2 * sin(tempo * 1.7 + n.z * 3.0);
        p.xy = c + vec2(cos(g), sin(g)) * r0 * (1.0 + (aRnd.z - 0.5) * 0.07);
      }
      p.z = 0.02;
      return p;
    }
    /* 19, la riga di oggi attraverso l'anello */
    luce *= 0.42 + 0.1 * sin(tempo * 0.9 + n.w * 6.0);
    p.xy += sulGiro(P, mix(rin, fuori, n.w)) + sulGiro(P + 0.25, (aRnd.z - 0.5) * ruotaR * 0.008);
    return p;
  }
  return p;
}
void main() {
  float tipo = aGal.w, r = aGal.x;
  vec3 g;
  if (tipo < 0.5) { float ang = aGal.y + tempo * 0.12 / (0.4 + r * 0.9); g = vec3(cos(ang) * r, aGal.z, sin(ang) * r); }
  else { float ang = aGal.y + tempo * 0.02; g = vec3(cos(ang) * r, aGal.z, sin(ang) * r); }
  g.yz = rot(1.08 + incl.y) * g.yz;
  g.xz = rot(-0.28 + incl.x) * g.xz;
  float lDa = aMDa.x, gDa = aMDa.y, lA = aMA.x, gA = aMA.y, tDa = aMDa.w, tA = aMA.w;
  vec3 pDa = anima(posto(aDa, aNDa), aNDa, lDa, gDa, tDa);
  vec3 pA = anima(posto(aA, aNA), aNA, lA, gA, tA);
  float s = smoothstep(aRnd.y * 0.4, aRnd.y * 0.4 + 0.6, passaggio);
  float testo = mix(step(10.5, aNDa.x) * step(aNDa.x, 12.5), step(10.5, aNA.x) * step(aNA.x, 12.5), s);
  float glifo = step(10.5, aNA.x) * (1.0 - step(11.5, aNA.x));
  float arco = sin(s * 3.1416) * min(1.0, distance(aDa, aA) * 40.0 + abs(aNDa.x - aNA.x));
  vec3 l = mix(pDa, pA, s);
  l.z += arco * (0.45 + aRnd.z * 0.6);
  l.xy += vec2(cos(aRnd.w * 6.28), sin(aRnd.w * 6.28)) * arco * 0.3;
  l.xy += vec2(sin(tempo * 0.6 + aRnd.y * 6.28), cos(tempo * 0.5 + aRnd.w * 6.28)) * 0.006;
  float partecipa = mix(aMDa.z, aMA.z, s);
  float k = smoothstep(aRnd.x * 0.45, aRnd.x * 0.45 + 0.55, morph) * partecipa;
  /* le forme stanno ferme sul loro posto della pagina mentre il cielo dondola: si toglie lo spostamento
     della camera a profondità zero; chi sta più avanti o più indietro scorre appena (parallasse) */
  l.xy += incl * vec2(0.9, -0.6) * (1.0 - l.z / cam) * (1.0 - testo);
  vec3 p = mix(g, l, k);
  vec2 d = p.xy - topo;
  /* morbida (2026-10-29): un vortice largo e lento; le forme composte lo sentono a metà, le lettere quasi niente */
  float f = exp(-dot(d, d) * 1.6) * forza * mix(1.0, 0.5, k) * (1.0 - testo * k * 0.85);
  p.xy = topo + rot(f * 0.38) * d * (1.0 + f * 0.04);
  float zc = cam - p.z;
  if (zc < 0.35) { gl_Position = vec4(3.0, 3.0, 0.0, 1.0); gl_PointSize = 0.0; vCol = vec3(0.0); vA = 0.0; return; }
  vec2 sp = (p.xy - incl * vec2(0.9, -0.6)) / zc * scala;
  sp.x /= aspetto;
  vec2 ndc = sp + centro;
  gl_Position = vec4(ndc, 0.0, 1.0);
  float tw = 0.62 + 0.38 * sin(tempo * (1.6 + aRnd.w * 2.2) + aRnd.y * 6.283);
  tw = mix(tw, 0.88, k * 0.8);
  float misuraT = mix(gDa, gA, s);
  float base = mix(1.0 + aRnd.z * aRnd.z * 3.4, 0.8 + aRnd.z * 1.0, testo * k);
  float lato = min(base * mix(1.0, misuraT, k) * tw * dpr * (cam / zc), 40.0 * dpr);
  gl_PointSize = lato;
  /* le stelle piccole agganciate ai pixel, ognuna con la sua posizione fissa dentro il pixel (come nell'apertura) */
  if (lato < 4.0) {
    vec2 fase = vec2(fract(aRnd.y * 13.7), fract(aRnd.w * 17.3)) - 0.5;
    ndc = (floor((ndc * 0.5 + 0.5) * ris) + 0.5 + fase) / ris * 2.0 - 1.0;
    gl_Position = vec4(ndc, 0.0, 1.0);
  }
  vec3 caldo = vec3(1.0, 0.86, 0.62), freddo = vec3(0.6, 0.7, 1.0), viola = vec3(0.8, 0.62, 1.0);
  vec3 c = mix(caldo, mix(freddo, viola, aRnd.w), smoothstep(0.25, 1.6, r));
  if (tipo > 0.5) c = mix(vec3(0.75, 0.78, 1.0), caldo, aRnd.w);
  vec3 ct = mix(colore(tDa), colore(tA), s);
  vCol = mix(c, ct, k);
  float peso = tipo > 0.5 ? 0.55 : 1.0;
  vA = (0.5 + 0.5 * tw) * mix(peso, mix(lDa, lA, s), k);
  float stretto = 0.0, morbido = 0.0;
  for (int i = 0; i < 4; i++) { stretto = max(stretto, dentro(ndc, zone[i], 0.035)); morbido = max(morbido, dentro(ndc, zone[i], 0.14)); }
  vA *= 1.0 - mix(morbido * 0.45, stretto * 0.95, k) * (1.0 - testo * k);
  vA *= mix(1.0, velo, glifo * s * k);
}`;
  const FSP = `#version 300 es
precision highp float;
in vec3 vCol; in float vA;
out vec4 o;
void main() {
  vec2 q = gl_PointCoord - 0.5;
  float d2 = dot(q, q) * 4.0;
  float a = (exp(-d2 * 5.5) + exp(-d2 * 22.0) * 0.8) * vA;
  o = vec4(vCol * a, a);
}`;
  /* la nebbia dell'apertura; in più l'alone della luna, che cresce con la luce */
  const FSN = `#version 300 es
precision highp float;
in vec2 vUv;
out vec4 o;
uniform float tempo, aspetto, luce;
uniform vec2 centro, incl;
uniform vec3 alone;
float h(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float rumore(vec2 p) { vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
  return mix(mix(h(i), h(i + vec2(1, 0)), f.x), mix(h(i + vec2(0, 1)), h(i + vec2(1, 1)), f.x), f.y); }
float fbm(vec2 p) { float s = 0.0, a = 0.5; for (int i = 0; i < 5; i++) { s += a * rumore(p); p = p * 2.03 + vec2(1.7, 9.2); a *= 0.5; } return s; }
void main() {
  vec2 p = (vUv - 0.5 - centro * 0.5) * vec2(aspetto, 1.0);
  float n = fbm(p * 1.4 + incl * 0.1 + vec2(tempo * 0.008, -tempo * 0.004));
  float n2 = fbm(p * 2.6 + n * 1.6 + incl * vec2(0.3, -0.2) - vec2(0.0, tempo * 0.012));
  float vig = smoothstep(1.4, 0.2, length((vUv - 0.5) * vec2(aspetto, 1.0)));
  vec3 c = vec3(0.010, 0.011, 0.026);
  c += vec3(0.26, 0.11, 0.42) * pow(n2, 3.2) * 0.75;
  c += vec3(0.04, 0.20, 0.27) * pow(n, 4.0) * 0.7;
  c += vec3(0.5, 0.36, 0.2) * pow(max(0.0, 1.0 - length(p) * 0.9), 6.0) * 0.08;
  c *= mix(0.55, 1.0, vig);
  /* l'alone: un velo di luce fredda attorno al disco, più largo e più chiaro con la luna piena */
  vec2 q = (vUv * 2.0 - 1.0 - alone.xy) * vec2(aspetto, 1.0);
  float dl = length(q) / max(alone.z, 0.001);
  c += vec3(0.62, 0.62, 0.7) * luce * (exp(-dl * dl * 1.1) * 0.05 + exp(-dl * 2.2) * 0.025) * smoothstep(0.85, 1.05, dl);
  c += (h(vUv * 917.0) - 0.5) * 0.012;
  o = vec4(c, 1.0);
}`;
  const VS = `#version 300 es
in vec2 aPos;
out vec2 vUv;
void main() { vUv = aPos * 0.5 + 0.5; gl_Position = vec4(aPos, 0.0, 1.0); }`;

  function compila(gl, tipo, src) {
    const s = gl.createShader(tipo);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s));
    return s;
  }
  function programma(gl, vs, fs, attributi) {
    const p = gl.createProgram();
    gl.attachShader(p, compila(gl, gl.VERTEX_SHADER, vs));
    gl.attachShader(p, compila(gl, gl.FRAGMENT_SHADER, fs));
    attributi.forEach((nome, i) => gl.bindAttribLocation(p, i, nome));
    gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(p));
    const u = {};
    const n = gl.getProgramParameter(p, gl.ACTIVE_UNIFORMS);
    for (let i = 0; i < n; i++) { const info = gl.getActiveUniform(p, i); u[info.name.replace(/\[0\]$/, '')] = gl.getUniformLocation(p, info.name); }
    return { p, u };
  }

  /* ── le forme del cielo vero, in coordinate locali (raggio 1) ── */
  const MARI = [[-0.58, 0.12, 0.32], [-0.45, -0.12, 0.24], [-0.28, 0.48, 0.24], [0.24, 0.47, 0.15], [0.38, 0.18, 0.17], [0.78, 0.3, 0.1], [0.62, -0.12, 0.13], [0.42, -0.28, 0.09], [-0.2, -0.36, 0.16], [-0.55, -0.4, 0.09], [-0.25, 0.78, 0.09], [0.05, 0.8, 0.09], [0.35, 0.75, 0.08], [0.05, 0.3, 0.07]];
  const CRATERI = [[-0.12, -0.72, 0.05], [-0.32, 0.18, 0.04], [-0.55, 0.15, 0.03], [-0.72, 0.38, 0.03]];
  /* il disco: tutte le stelle possibili della luna, accese o spente dallo shader secondo la fase */
  function disco(n) {
    const out = [];
    while (out.length < n) {
      const x = Math.random() * 2 - 1, y = Math.random() * 2 - 1, d = x * x + y * y;
      if (d > 1) continue;
      let mare = 0, cratere = 0;
      for (const [mx, my, mr] of MARI) mare = Math.max(mare, Math.exp((-((x - mx) ** 2 + (y - my) ** 2) / (mr * mr)) * 1.4));
      for (const [cx, cy, cr] of CRATERI) cratere = Math.max(cratere, Math.exp(-((x - cx) ** 2 + (y - cy) ** 2) / (cr * cr)));
      out.push({ x, y, z: Math.sqrt(1 - d) * 0.2, lum: (1 - 0.62 * mare) * (1 + cratere * 1.1), gr: 0.85 + cratere * 0.5, tinta: mare > 0.5 ? 5 : 0, anim: 13, fase: Math.random() / (1 - 0.55 * mare), p1: x, p2: y });
    }
    return out;
  }
  /* la ruota dell'anno (tipi 16-19): le posizioni le calcola lo shader dal centro della ruota, qui solo i
     parametri. I due bordi dell'anello sono più fitti (quello di dentro è un cerchio, quello di fuori segue la
     luce), il mezzo più rado */
  function anelloDellaLuce(n, nodi) {
    const nTacche = Math.round(n * 0.1), nSole = Math.round(n * 0.06), nOggi = Math.round(n * 0.02), nAnello = n - nTacche - nSole - nOggi;
    const out = [];
    const stella = (anim, lum, gr, tinta, p1, p2) => ({ x: 0, y: 0, z: 0, lum, gr, tinta, anim, fase: Math.random(), p1, p2 });
    for (let i = 0; i < nAnello; i++) {
      const q = Math.random(), bordo = q < 0.62;
      const f = q < 0.3 ? Math.abs(gauss()) * 0.02 : bordo ? 1 - Math.abs(gauss()) * 0.02 : Math.random();
      out.push(stella(16, bordo ? 0.9 : 0.6, bordo ? 0.82 : 0.72, 2, Math.random(), f));
    }
    for (let i = 0; i < nTacche; i++) out.push(stella(17, 0.85, 0.85, 0, nodi[i % 8], -0.12 + Math.random() * 1.24));
    for (let i = 0; i < nSole; i++) {
      const anello = i > nSole * 0.25;
      out.push(stella(18, anello ? 0.95 : 1.25, anello ? 0.8 : 1.1, 0, ((i + Math.random() * 0.6) / nSole) * Math.PI * 6, anello ? 1 : 0));
    }
    for (let i = 0; i < nOggi; i++) out.push(stella(19, 0.8, 0.7, 2, 0, Math.random()));
    return out;
  }

  /* ── una radice: la sezione della pagina iniziale, il cielo della pagina della luna o la sua ruota ── */
  function accendiRadice(radice) {
    const canvas = radice.querySelector('.kg-luna__cielo');
    const postoLuna = radice.querySelector('[data-kg-posto="luna"]');
    const postoRuota = radice.querySelector('[data-kg-posto="ruota"]');
    /* la pagina della luna usa la luna sul mare di kg-forme.js: otto sagome, una per fase */
    const mare = radice.dataset.kgForma === 'mare' ? window.KG_FORME?.mare || null : null;
    const piccolo = radice.clientWidth < 700;
    const stato = { luna: null, festa: null, ruota: null };
    const api = {};

    /* i parametri del cielo, dal giorno scelto (adesso, o un giorno della pagina) */
    const obiettivi = { fase: 0, anno: 0, festa: 0 };
    const vicino = (x, a, giro) => x + Math.round((a - x) / giro) * giro;
    function cielo(l, festa) {
      if (l) obiettivi.fase = l.angolo;
      if (l && mare) { stato.kMare = mare.indice(l.chiave); obiettivi.fase = mare.angolo(stato.kMare); }
      if (festa) { obiettivi.anno = festa.anno; obiettivi.festa = festa.u; stato.ruota = festa; }
    }
    api.cielo = cielo;

    let gl = canvas?.getContext('webgl2', { alpha: false, antialias: false, depth: false, stencil: false, premultipliedAlpha: false, powerPreference: 'high-performance' });
    let prPunti, prNebbia, nebbia;
    if (gl) {
      try {
        prPunti = programma(gl, VSP, FSP, ['aGal', 'aRnd', 'aDa', 'aA', 'aMDa', 'aMA', 'aNDa', 'aNA']);
        prNebbia = programma(gl, VS, piccolo ? FSN.replace('i < 5; i++', 'i < 3; i++') : FSN, ['aPos']);
        const vaoN = gl.createVertexArray();
        gl.bindVertexArray(vaoN);
        gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
        gl.enableVertexAttribArray(0);
        gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
        gl.bindVertexArray(null);
        nebbia = () => { gl.bindVertexArray(vaoN); gl.drawArrays(gl.TRIANGLES, 0, 3); gl.bindVertexArray(null); };
        radice.classList.add('kg-luna--gl');
      } catch (e) {
        console.error('[kg-luna]', e);
        gl = null;
      }
    }

    /* ── stato del motore ── */
    let vao, N = 0, dati, indiciLuna = [], indiciRuota = [], indiciTesto = [];
    /* le forme si generano una volta sola: quando la pagina cambia misura si spostano, non si rifanno */
    let formaLuna = [], formaRuota = [], nodiRuota = '';
    const formeMare = new Map();
    let kScritto = -1, scenaVista = '';
    const buf = {};
    let aspetto = 1, risoluzione = 1;
    const centro = { x: 0, y: 0 };
    const incl = { x: molla(0, 22, 9.4), y: molla(0, 22, 9.4) };
    const topo = { x: molla(0, 12, 6.9), y: molla(0, 12, 6.9) };
    let forza = 0;
    const zone = new Float32Array(16).fill(FUORI);
    const alone = [FUORI, FUORI, 0.1];
    let morph = 0, morphObj = 0, passaggio = 1, tempoOra = 0, visibile = false, partito = false, acceso = false;
    let ripasso = 1, ripassoDa = 0;
    const fase = molla(0, 30, 11);
    let anno = 0, festaU = 0;
    let velo = 1, veloObj = 1, sopraCta = 0, sopraCtaObj = 0, giroCta = 0, controllato = 0;

    const P = { x: 0.5, y: 0.5, dentro: false };
    radice.addEventListener('pointermove', (e) => {
      if (e.pointerType === 'touch') return;
      const r = radice.getBoundingClientRect();
      P.x = (e.clientX - r.left) / r.width; P.y = (e.clientY - r.top) / r.height; P.dentro = true;
    }, { passive: true });
    radice.addEventListener('pointerleave', () => { P.dentro = false; });

    function particelle() {
      /* la luna della home, più grande dal 2026-10-30, ne vuole quante la luna sul mare per restare fitta */
      N = postoLuna ? (piccolo ? (mare ? 16000 : 20000) : 40000) : (piccolo ? 9500 : 16000);
      dati = {
        gal: new Float32Array(N * 4), rnd: new Float32Array(N * 4),
        da: new Float32Array(N * 3), a: new Float32Array(N * 3),
        mDa: new Float32Array(N * 4), mA: new Float32Array(N * 4),
        nDa: new Float32Array(N * 4), nA: new Float32Array(N * 4),
      };
      const indici = [];
      const R = 2.05;
      for (let i = 0; i < N; i++) {
        if (Math.random() < 0.14) {
          const r = 0.6 + Math.random() * 3.4;
          dati.gal.set([r, Math.random() * Math.PI * 2, gauss() * 0.9, 1], i * 4);
        } else {
          const braccio = i % 3, t = Math.pow(Math.random(), 2.0), r = 0.05 + t * R;
          const disp = (Math.random() - 0.5) * (1.05 / (0.4 + r)) + gauss() * 0.16;
          dati.gal.set([r, braccio * (Math.PI * 2 / 3) + r * 2.15 + disp, gauss() * 0.09 * (1.3 - t), 0], i * 4);
          indici.push(i);
        }
        dati.rnd.set([Math.random(), Math.random(), Math.random(), Math.random()], i * 4);
      }
      /* tutte le stelle dei bracci vanno a una forma o al testo, come nell'apertura: se ne restassero nella
         galassia, il suo nucleo resterebbe acceso proprio dietro la luna */
      /* la piccola ruota della home ha la stessa densità di quella della pagina (circa 0,2 stelle per pixel) */
      const pesi = [postoLuna ? (mare ? 0.75 : 0.66) : 0, postoRuota ? (postoLuna ? (piccolo ? 0.08 : 0.045) : 0.72) : 0, 0.25];
      const somma = pesi.reduce((t, p) => t + p, 0);
      let da = 0;
      [indiciLuna, indiciRuota, indiciTesto] = pesi.map((p, i) => { const q = i === 2 ? indici.length - da : Math.round(indici.length * p / somma); const s = indici.slice(da, da + q); da += q; return s; });
      vao = gl.createVertexArray();
      gl.bindVertexArray(vao);
      [['gal', 4], ['rnd', 4], ['da', 3], ['a', 3], ['mDa', 4], ['mA', 4], ['nDa', 4], ['nA', 4]].forEach(([nome, n], loc) => {
        buf[nome] = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, buf[nome]);
        gl.bufferData(gl.ARRAY_BUFFER, dati[nome], gl.DYNAMIC_DRAW);
        gl.enableVertexAttribArray(loc);
        gl.vertexAttribPointer(loc, n, gl.FLOAT, false, 0, 0);
      });
      gl.bindVertexArray(null);
    }
    function carica(nomi) {
      for (const nomeBuf of nomi) { gl.bindBuffer(gl.ARRAY_BUFFER, buf[nomeBuf]); gl.bufferSubData(gl.ARRAY_BUFFER, 0, dati[nomeBuf]); }
    }

    /* ── dove stanno le cose: i posti delle forme e le zone di testo, misurati dalla pagina ── */
    const SCANSA = ['[data-kg-scansa="1"]', '[data-kg-scansa="2"]', '[data-kg-scansa="3"]', '[data-kg-scansa="4"]'];
    const rettangolo = (r, b) => [(r.left - b.left) / b.width * 2 - 1, 1 - (r.bottom - b.top) / b.height * 2, (r.right - b.left) / b.width * 2 - 1, 1 - (r.top - b.top) / b.height * 2];
    let misure = null;
    function misura() {
      const b = radice.getBoundingClientRect();
      if (!b.width || !b.height) return false;
      aspetto = b.width / b.height;
      zone.fill(FUORI);
      SCANSA.forEach((sel, i) => {
        const r = radice.querySelector(sel)?.getBoundingClientRect();
        if (r && r.width && r.height) zone.set(rettangolo(r, b), i * 4);
      });
      const posto = (el) => {
        const q = el?.getBoundingClientRect();
        if (!q || !q.width) return null;
        const cx = ((q.left + q.right) / 2 - b.left) / b.width * 2 - 1, cy = 1 - ((q.top + q.bottom) / 2 - b.top) / b.height * 2;
        return { cx, cy, r: (Math.min(q.width, q.height) / 2) / b.height * 2 };
      };
      misure = { luna: posto(postoLuna), ruota: posto(postoRuota), b: [b.width, b.height] };
      /* la luna sul mare: l'orizzonte è la riga [data-kg-orizzonte] della pagina */
      const oriz = radice.querySelector('[data-kg-orizzonte]')?.getBoundingClientRect();
      if (oriz && oriz.width) misure.orizzonte = 1 - (oriz.top - b.top) / b.height * 2;
      /* la galassia gira attorno alla forma principale */
      const capo = misure.luna || misure.ruota;
      if (capo) { centro.x = capo.cx; centro.y = capo.cy; }
      /* l'alone attorno al disco: il raggio è in unità dell'altezza (lo shader corregge la larghezza) */
      if (misure.luna) { alone[0] = misure.luna.cx; alone[1] = misure.luna.cy; alone[2] = misure.luna.r * (mare ? 1 : 0.95); }
      return true;
    }
    /* da un punto dello schermo (NDC) a un punto del cielo a profondità zero */
    const mondo = (cx, cy) => [(cx - centro.x) * aspetto / SCALA * CAM, (cy - centro.y) / SCALA * CAM];

    /* scrive le forme nei bersagli (le stesse in «Da» e in «A»: non cambiano, cambia solo la luce) */
    function scriviForme(ancheDa = true) {
      const { a, mA, nA } = dati;
      const metti = (indici, lista, cx, cy, s) => {
        indici.forEach((i, j) => {
          const p = lista[j % lista.length];
          const relativo = p.p2 > 2.5;
          a.set([cx + (relativo ? 0 : p.x * s), cy + (relativo ? 0 : p.y * s), p.z * s], i * 3);
          mA.set([p.lum, p.gr, 1, p.tinta], i * 4);
          nA.set([p.anim, p.fase, p.anim === 13 ? p.p1 : relativo ? p.p1 * s : p.p1, p.p2], i * 4);
        });
      };
      if (misure.luna && mare) {
        /* la luna sul mare a tutto sfondo: il disco dove la pagina mette il suo posto, l'orizzonte da un
           bordo all'altro, il mare e la scia fino in fondo. Per la stessa fase e la stessa pagina la scena è
           sempre la stessa; se la pagina cambia misura si rifà. */
        const k = stato.kMare ?? 0;
        const [mx, my] = mondo(misure.luna.cx, misure.luna.cy), r = misure.luna.r / SCALA * CAM;
        const or = mondo(0, misure.orizzonte ?? misure.luna.cy - misure.luna.r * 1.4)[1];
        const o = { mx, my, r, or, fondo: mondo(0, -1)[1] - 0.02, x0: mondo(-1.02, 0)[0], x1: mondo(1.02, 0)[0] };
        const scena = Object.values(o).map((v) => v.toFixed(3)).join();
        if (scena !== scenaVista) { formeMare.clear(); scenaVista = scena; }
        if (!formeMare.has(k)) {
          const lista = mare.scena(indiciLuna.length, k, o);
          while (lista.length < indiciLuna.length) { const p = lista[(Math.random() * lista.length) | 0]; lista.push({ ...p, x: p.x + gauss() * 0.004, y: p.y + gauss() * 0.004 }); }
          formeMare.set(k, lista);
        }
        metti(indiciLuna, formeMare.get(k), 0, 0, 1);
        kScritto = k;
      } else if (misure.luna) {
        if (!formaLuna.length) formaLuna = disco(indiciLuna.length);
        const [cx, cy] = mondo(misure.luna.cx, misure.luna.cy), R = misure.luna.r / SCALA * CAM;
        metti(indiciLuna, formaLuna, cx, cy, R * 0.95);
      }
      if (misure.ruota) {
        const nodi = stato.ruota?.nodi || [0, 0.125, 0.25, 0.375, 0.5, 0.625, 0.75, 0.875];
        if (nodi.join() !== nodiRuota) { formaRuota = anelloDellaLuce(indiciRuota.length, nodi); nodiRuota = nodi.join(); }
        const [cx, cy] = mondo(misure.ruota.cx, misure.ruota.cy), R = misure.ruota.r / SCALA * CAM;
        metti(indiciRuota, formaRuota, cx, cy, R * 0.9);
      }
      if (ancheDa) for (const i of [...indiciLuna, ...indiciRuota]) {
        dati.da.set(a.subarray(i * 3, i * 3 + 3), i * 3); dati.mDa.set(mA.subarray(i * 4, i * 4 + 4), i * 4); dati.nDa.set(nA.subarray(i * 4, i * 4 + 4), i * 4);
      }
    }

    /* ── il testo fatto di stelle (come nell'apertura) ── */
    const CAMPIONE = 2;
    const foglio = document.createElement('canvas');
    const penna = foglio.getContext('2d', { willReadFrequently: true });
    function inchiostro(el) {
      const q = el.getBoundingClientRect();
      if (!q.width || !q.height) return [];
      const x0 = Math.floor(q.left) - 2, y0 = Math.floor(q.top) - 2;
      const W = Math.ceil((q.width + 4) * CAMPIONE), H = Math.ceil((q.height + 4) * CAMPIONE);
      if (foglio.width < W) foglio.width = W;
      if (foglio.height < H) foglio.height = H;
      penna.setTransform(1, 0, 0, 1, 0, 0);
      penna.clearRect(0, 0, foglio.width, foglio.height);
      penna.setTransform(CAMPIONE, 0, 0, CAMPIONE, -x0 * CAMPIONE, -y0 * CAMPIONE);
      penna.fillStyle = '#fff';
      penna.textBaseline = 'alphabetic';
      const r = document.createRange();
      const giro = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
      for (let n = giro.nextNode(); n; n = giro.nextNode()) {
        const st = getComputedStyle(n.parentElement);
        penna.font = `${st.fontStyle} ${st.fontWeight} ${st.fontSize} ${st.fontFamily}`;
        const m = penna.measureText('Hg');
        const sale = m.fontBoundingBoxAscent ?? parseFloat(st.fontSize) * 0.8, scende = m.fontBoundingBoxDescent ?? parseFloat(st.fontSize) * 0.2;
        const maiuscole = st.textTransform === 'uppercase';
        const t = n.nodeValue;
        for (let i = 0; i < t.length; i++) {
          let fine = i + 1;
          if (/[\uD800-\uDBFF]/.test(t[i])) fine++;
          const ch = t.slice(i, fine);
          if (/\s/.test(ch)) continue;
          r.setStart(n, i); r.setEnd(n, fine);
          const c = r.getBoundingClientRect();
          i = fine - 1;
          if (!c.width) continue;
          penna.fillText(maiuscole ? ch.toUpperCase() : ch, c.left, c.top + (c.height - sale - scende) / 2 + sale);
        }
      }
      const img = penna.getImageData(0, 0, W, H).data;
      const punti = [];
      for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) if (img[(y * W + x) * 4 + 3] > 110) punti.push(x0 + (x + 0.5) / CAMPIONE, y0 + (y + 0.5) / CAMPIONE);
      return punti;
    }
    function contorno(el, quanti) {
      const q = el.getBoundingClientRect();
      const rr = Math.min(q.height / 2, parseFloat(getComputedStyle(el).borderTopLeftRadius) || 0);
      const x0 = q.left + 0.5, y0 = q.top + 0.5, w = q.width - 1, h = q.height - 1, a = Math.max(0, w - 2 * rr), b = Math.max(0, h - 2 * rr);
      const arco = Math.PI * rr / 2;
      const ang = (cx, cy, al) => [cx + Math.cos(al) * rr, cy + Math.sin(al) * rr];
      const tratti = [
        [a, (t) => [x0 + rr + t * a, y0]], [arco, (t) => ang(x0 + w - rr, y0 + rr, -Math.PI / 2 + t * Math.PI / 2)],
        [b, (t) => [x0 + w, y0 + rr + t * b]], [arco, (t) => ang(x0 + w - rr, y0 + h - rr, t * Math.PI / 2)],
        [a, (t) => [x0 + w - rr - t * a, y0 + h]], [arco, (t) => ang(x0 + rr, y0 + h - rr, Math.PI / 2 + t * Math.PI / 2)],
        [b, (t) => [x0, y0 + h - rr - t * b]], [arco, (t) => ang(x0 + rr, y0 + rr, Math.PI + t * Math.PI / 2)],
      ];
      const giroTot = tratti.reduce((s, [l]) => s + l, 0);
      const punti = [];
      for (let k = 0; k < quanti; k++) {
        const u = (k + Math.random() * 0.6) / quanti;
        let d = u * giroTot;
        for (let t = 0; t < tratti.length; t++) {
          const [l, f] = tratti[t];
          if (d <= l || t === tratti.length - 1) { const [x, y] = f(l ? Math.min(1, d / l) : 0); punti.push({ x: x + gauss() * 0.35, y: y + gauss() * 0.35, u }); break; }
          d -= l;
        }
      }
      return punti;
    }
    const scritti = () => [...radice.querySelectorAll('[data-kg-scrivi]')].filter((el) => el.getClientRects().length && !el.closest('[hidden]'));
    const bordi = () => [...radice.querySelectorAll('[data-kg-bordo]')].filter((el) => el.getClientRects().length && !el.closest('[hidden]'));
    let postoVisto = '';
    const postoTesto = () => {
      const b = radice.getBoundingClientRect();
      return [...scritti(), ...bordi()].map((el) => { const q = el.getBoundingClientRect(); return [q.left - b.left, q.top - b.top, q.width, q.height].map(Math.round).join(',') + el.textContent.length; }).join(';');
    };
    function campiona() {
      const b = radice.getBoundingClientRect();
      postoVisto = postoTesto();
      if (!b.width || !b.height || !indiciTesto.length) return [];
      const ndc = (x, y) => [(x - b.left) / b.width * 2 - 1, 1 - (y - b.top) / b.height * 2];
      let resto = indiciTesto.length;
      const punti = [];
      for (const el of bordi()) {
        const q = el.getBoundingClientRect();
        const quanti = Math.min(Math.round(resto * 0.16), Math.round((2 * (q.width + q.height)) / (piccolo ? 1.35 : 1.15)));
        for (const p of contorno(el, quanti)) punti.push({ xy: ndc(p.x, p.y), lum: 0.8, gr: 1.0, tinta: 2, anim: 12, u: p.u });
        resto -= quanti;
      }
      const els = scritti();
      const inchiostri = els.map(inchiostro);
      const pesi = els.map((el, i) => (inchiostri[i].length / 2) * (parseFloat(el.dataset.kgPeso) || 1));
      const somma = pesi.reduce((s, v) => s + v, 0);
      els.forEach((el, i) => {
        const ink = inchiostri[i], n = ink.length / 2;
        if (!n || !somma) return;
        const quanti = Math.round(resto * pesi[i] / somma), fine = el.dataset.kgPeso === '0.85';
        for (let k = 0; k < quanti; k++) {
          const j = (Math.random() * n) | 0;
          const x = ink[j * 2] + (Math.random() - 0.5) / CAMPIONE, y = ink[j * 2 + 1] + (Math.random() - 0.5) / CAMPIONE;
          punti.push({ xy: ndc(x, y), lum: fine ? 0.66 : 0.62, gr: fine ? 0.85 : 1.05, tinta: fine ? 7 : 0, anim: 11, u: 0 });
        }
      });
      return punti;
    }
    function scriviTesto(punti, ancheDa) {
      const { a, mA, nA } = dati;
      for (let j = 0; j < indiciTesto.length; j++) {
        const i = indiciTesto[j], p = punti[j];
        if (p) { a.set([p.xy[0], p.xy[1], 0], i * 3); mA.set([p.lum, p.gr, 1, p.tinta], i * 4); nA.set([p.anim, Math.random(), p.u, 0], i * 4); }
        else { a.set([0, 0, 0], i * 3); mA.set([0, 1, 0, 0], i * 4); nA.set([0, 0, 0, 0], i * 4); }
      }
      if (ancheDa) for (const i of indiciTesto) { dati.da.set(a.subarray(i * 3, i * 3 + 3), i * 3); dati.mDa.set(mA.subarray(i * 4, i * 4 + 4), i * 4); dati.nDa.set(nA.subarray(i * 4, i * 4 + 4), i * 4); }
    }
    /* tutto da capo: misure, forme, testo */
    function componi() {
      if (!gl || !misura()) return;
      scriviForme();
      scriviTesto(campiona(), true);
      carica(['a', 'mA', 'nA', 'da', 'mDa', 'nDa']);
    }
    /* un altro giorno: le stelle delle lettere volano sulle lettere nuove e, se la fase è un'altra, quelle
       della luna sulla sagoma nuova (con il movimento ridotto la scena si ricompone e basta) */
    api.riscrivi = () => {
      if (!gl || !dati) return;
      if (fermo || !partito || morph < 0.95) { componi(); return; }
      const cambia = mare && (stato.kMare ?? 0) !== kScritto;
      /* passata Yule la prossima festa è dell'anno dopo: le feste del fuoco cadono appena altrove */
      const cambiaRuota = !!misure?.ruota && (stato.ruota?.nodi || []).join() !== nodiRuota;
      for (const i of [...indiciTesto, ...(cambia ? indiciLuna : []), ...(cambiaRuota ? indiciRuota : [])]) { dati.da.set(dati.a.subarray(i * 3, i * 3 + 3), i * 3); dati.mDa.set(dati.mA.subarray(i * 4, i * 4 + 4), i * 4); dati.nDa.set(dati.nA.subarray(i * 4, i * 4 + 4), i * 4); }
      if ((cambia || cambiaRuota) && misura()) scriviForme(false);
      scriviTesto(campiona(), false);
      carica(['a', 'mA', 'nA', 'da', 'mDa', 'nDa']);
      passaggio = 0;
    };

    /* ── l'arrivo: le stelle lasciano la galassia, la luna ripassa il mese fino a stanotte ── */
    function parti() {
      if (partito) return;
      partito = true;
      morphObj = 1;
      if (fermo) { morph = 1; ripasso = 1; fase.v = obiettivi.fase; anno = obiettivi.anno; festaU = obiettivi.festa; accendi(); velo = VELO; return; }
      ripasso = 0;
      ripassoDa = 0.02;
      anno = obiettivi.anno - 0.06;
    }
    function accendi() {
      if (acceso) return;
      acceso = true;
      radice.classList.add('kg-luna--accesa');
      veloObj = VELO;
    }
    api.avvia = () => { if (!gl) { radice.classList.add('kg-luna--accesa'); return; } componi(); if (fermo) { parti(); disegna(performance.now() / 1000, 0.016); } };

    /* ── un fotogramma ── */
    function disegna(t, dt) {
      tempoOra = t;
      const r = canvas.getBoundingClientRect();
      const kk = dpr() * risoluzione;
      let w = Math.max(2, Math.round(r.width * kk)), h = Math.max(2, Math.round(r.height * kk));
      const c = Math.min(1, Math.sqrt((piccolo ? 1300000 : 2400000) / (w * h)));
      w = Math.round(w * c); h = Math.round(h * c);
      if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }
      incl.x.obiettivo = P.dentro ? (P.x - 0.5) * 0.4 : Math.sin(t * 0.13) * 0.1;
      incl.y.obiettivo = P.dentro ? (P.y - 0.5) * 0.24 : Math.sin(t * 0.09 + 1.3) * 0.04;
      incl.x.passo(dt); incl.y.passo(dt);
      const mx = (P.x * 2 - 1 - centro.x) * aspetto * CAM / SCALA, my = (-(P.y * 2 - 1) - centro.y) * CAM / SCALA;
      if (P.dentro) {
        if (forza < 0.02) { topo.x.v = mx; topo.y.v = my; topo.x.vel = 0; topo.y.vel = 0; }
        topo.x.obiettivo = mx; topo.y.obiettivo = my;
      }
      forza += ((P.dentro ? 1 : 0) - forza) * Math.min(1, dt * 1.1);
      topo.x.passo(dt); topo.y.passo(dt);
      velo += (veloObj - velo) * Math.min(1, dt * 4);
      sopraCta += (sopraCtaObj - sopraCta) * Math.min(1, dt * 5);
      giroCta = (giroCta + dt * (0.12 + sopraCta * 0.22)) % 1;
      if (t - controllato > 0.5) {
        controllato = t;
        if (passaggio >= 1 && postoTesto() !== postoVisto) componi();
      }
      if (!fermo) {
        morph += Math.sign(morphObj - morph) * Math.min(Math.abs(morphObj - morph), dt / VOLO);
        passaggio = Math.min(1, passaggio + dt / 0.9);
        if (partito && !acceso && morph >= 0.9) accendi();
        if (ripasso < 1) {
          /* il mese ripassa: la luce cresce dalla luna nuova, si fa piena e cala fino a stanotte */
          ripasso = Math.min(1, ripasso + dt / RIPASSO);
          const u = liscia(Math.min(1, ripasso * 1.05));
          fase.v = ripassoDa + (obiettivi.fase - ripassoDa) * u; fase.vel = 0;
        } else {
          /* il disegno è periodico: la luna va per la strada più breve, anche a cavallo della luna nuova */
          fase.obiettivo = vicino(obiettivi.fase, fase.v, Math.PI * 2);
          fase.passo(dt);
        }
        /* il sole va sempre avanti: a Yule l'anno ricomincia da 0 ma il disegno è periodico (lo shader usa fract) */
        anno += (vicino(obiettivi.anno, anno, 1) - anno) * Math.min(1, dt * (partito ? 0.9 : 0));
        festaU = obiettivi.festa;
      }
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.disable(gl.BLEND);
      gl.useProgram(prNebbia.p);
      const un = prNebbia.u;
      gl.uniform1f(un.tempo, t); gl.uniform1f(un.aspetto, aspetto);
      gl.uniform2f(un.centro, centro.x, centro.y); gl.uniform2f(un.incl, incl.x.v, incl.y.v);
      gl.uniform3f(un.alone, alone[0], alone[1], alone[2]);
      gl.uniform1f(un.luce, misure?.luna ? (1 - Math.cos(fase.v)) / 2 * Math.min(1, morph * 1.4) : 0);
      nebbia();
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.ONE, gl.ONE);
      const u = prPunti.u;
      gl.useProgram(prPunti.p);
      gl.uniform1f(u.tempo, t); gl.uniform1f(u.morph, liscia(morph)); gl.uniform1f(u.passaggio, passaggio);
      gl.uniform1f(u.aspetto, aspetto); gl.uniform1f(u.dpr, dpr() * risoluzione * c); gl.uniform1f(u.scala, SCALA); gl.uniform1f(u.cam, CAM);
      gl.uniform2f(u.topo, topo.x.v, topo.y.v); gl.uniform2f(u.centro, centro.x, centro.y); gl.uniform2f(u.incl, incl.x.v, incl.y.v);
      gl.uniform1f(u.forza, forza); gl.uniform2f(u.ris, canvas.width, canvas.height);
      gl.uniform1f(u.velo, velo); gl.uniform1f(u.giroCta, giroCta); gl.uniform1f(u.sopraCta, sopraCta);
      gl.uniform1f(u.faseL, fase.v); gl.uniform1f(u.annoP, anno); gl.uniform1f(u.festaU, festaU);
      gl.uniform1f(u.ruotaR, misure?.ruota ? (misure.ruota.r / SCALA) * CAM * 0.9 : 0);
      gl.uniform4fv(u.zone, zone);
      gl.bindVertexArray(vao);
      gl.drawArrays(gl.POINTS, 0, N);
      gl.bindVertexArray(null);
      gl.disable(gl.BLEND);
    }

    let attivo = false, ultimo = 0, misurati = 0, sommaDt = 0;
    function fotogramma(ora) {
      if (!visibile || document.hidden) { attivo = false; return; }
      const dt = Math.min(0.05, Math.max(0.001, (ora - ultimo) / 1000));
      ultimo = ora;
      if (misurati < 150) { misurati++; sommaDt += dt; if (misurati === 150 && sommaDt / 150 > 1 / 28) risoluzione = 0.7; }
      disegna(ora / 1000, dt);
      requestAnimationFrame(fotogramma);
    }
    function gira() {
      if (!gl || !dati) return;
      if (fermo) { disegna(performance.now() / 1000, 0.016); return; }
      if (visibile && !document.hidden && !attivo) { attivo = true; ultimo = performance.now(); requestAnimationFrame(fotogramma); }
    }
    api.ridisegna = () => { if (fermo && gl && dati) { fase.v = obiettivi.fase; anno = obiettivi.anno; festaU = obiettivi.festa; disegna(performance.now() / 1000, 0.016); } };

    for (const el of radice.querySelectorAll('[data-kg-bordo]')) {
      for (const [ev, v] of [['pointerenter', 1], ['pointerleave', 0], ['focus', 1], ['blur', 0]]) el.addEventListener(ev, () => { sopraCtaObj = v; });
    }
    if (gl) {
      particelle();
      let rifai = 0;
      const ridisponi = () => { cancelAnimationFrame(rifai); rifai = requestAnimationFrame(() => { componi(); if (fermo) api.ridisegna(); }); };
      new ResizeObserver(ridisponi).observe(radice);
      document.fonts?.ready.then(ridisponi);
    }
    /* parte quando se ne vede un terzo (o tutta, se è più bassa dello schermo) */
    new IntersectionObserver(([v]) => {
      visibile = v.isIntersecting;
      if (v.intersectionRatio >= 0.3 || v.intersectionRect.height > window.innerHeight * 0.45) parti();
      gira();
    }, { threshold: [0, 0.01, 0.15, 0.3, 0.5] }).observe(radice);
    document.addEventListener('visibilitychange', gira);
    radice.kgStato = () => ({ gl: !!gl, partito, acceso, morph: +morph.toFixed(2), ripasso: +ripasso.toFixed(2), fase: +fase.v.toFixed(3), anno: +anno.toFixed(3), festa: festaU, stelle: N, testo: indiciTesto.length, risoluzione });
    return api;
  }

  /* ── i testi: la luna di un giorno, la prossima festa ── */
  const sciogli = document.createElement('textarea');
  function avvia(radice) {
    let conf = {};
    try { conf = inChiaro(JSON.parse(radice.querySelector('[data-kg-luna-testi]')?.textContent || '{}')); } catch (e) { /* senza testi non c'è niente da mostrare */ }
    const lingua = conf.lingua === 'en' ? 'en-GB' : conf.lingua || undefined;
    const campo = (nome) => radice.querySelectorAll(`[data-kg-campo="${nome}"]`);
    const metti = (nome, testo) => campo(nome).forEach((el) => { sciogli.innerHTML = testo || ''; el.textContent = sciogli.value; });
    const leva = (nome, voce) => campo(nome).forEach((a) => {
      if (!voce?.url || !voce?.title) { a.hidden = true; return; }
      a.href = voce.url; a.hidden = false;
      const t = a.querySelector('[data-kg-leva-testo]') || a;
      t.textContent = voce.title;
    });
    const fmt = (opzioni) => { try { return new Intl.DateTimeFormat(lingua, opzioni); } catch (e) { return new Intl.DateTimeFormat(undefined, opzioni); } };
    const giornoBreve = fmt({ weekday: 'short', day: 'numeric', month: 'short' });
    const giornoOra = fmt({ weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
    const giornoLungo = fmt({ day: 'numeric', month: 'long' });
    const relativo = (() => { try { return new Intl.RelativeTimeFormat(lingua, { numeric: 'auto' }); } catch (e) { return null; } })();
    const percento = (() => { try { return new Intl.NumberFormat(lingua, { style: 'percent', maximumFractionDigits: 0 }); } catch (e) { return { format: (x) => `${Math.round(x * 100)}%` }; } })();
    const motore = accendiRadice(radice);

    /* la ruota: dove sta ogni festa sul giro dell'anno */
    function ruotaDi(data) {
      const festa = CIELO.prossimaFesta(data);
      if (!festa) return null;
      const anno = festa.quando.getFullYear();
      const tutte = CIELO.feste(anno);
      const u = (d) => CIELO.annoSullaRuota(d);
      return { festa, nodi: tutte.map((f) => u(f.esatta ? f.quando : new Date(f.quando.getTime() + 12 * 36e5))), u: u(festa.esatta ? festa.quando : new Date(festa.quando.getTime() + 12 * 36e5)), anno: u(data) };
    }

    function mostra(data, { quando } = {}) {
      const l = CIELO.luna(data);
      const s = CIELO.segnoLuna(data);
      const f = conf.fasi?.[l.chiave] || {};
      metti('nome', f.name);
      metti('frase', f.line);
      const dati = [(conf.luce || '§').replace('§', percento.format(l.luce)), (conf.nelSegno || '§').replace('§', conf.segni?.[s.segno] || '')];
      if (conf.giorno && radice.dataset.kgLuna === 'pagina') dati.splice(1, 0, conf.giorno.replace('§', Math.floor(l.eta) + 1));
      metti('dati', dati.filter(Boolean).join(' · '));
      metti('quando', quando || '');
      const dove = (q) => (radice.dataset.kgLuna === 'pagina' ? giornoOra : giornoBreve).format(q);
      /* le prossime lune sono quelle di adesso: un giorno scelto nella pagina non le cambia */
      if (!quando && l.nuova) metti('nuova', dove(l.nuova));
      if (!quando && l.piena) metti('piena', dove(l.piena));
      leva('leva-fase', conf.leve?.[l.chiave]);
      leva('leva-segno', conf.leveSegni?.[s.segno] && { url: conf.leveSegni[s.segno], title: (conf.segnoLeva || '§').replace('§', conf.segni?.[s.segno] || '') });
      /* il disegno in SVG (senza WebGL2): il lembo acceso a destra quando cresce */
      radice.querySelectorAll('[data-kg-svg-luna]').forEach((p) => p.setAttribute('d', terminatore(l.elong / 360, Number(p.dataset.kgSvgLuna) || 32)));
      radice.querySelectorAll('[data-kg-svg-mese]').forEach((c) => c.style.setProperty('--kg-fatto', (l.eta / CIELO.SINODICO).toFixed(4)));
      return l;
    }
    function mostraFesta(data) {
      const r = ruotaDi(data);
      if (!r) return null;
      const { festa } = r;
      const x = conf.feste?.[festa.chiave] || {};
      const g0 = CIELO.inizioGiorno(data), g1 = CIELO.inizioGiorno(festa.quando);
      const mancano = Math.round((g1 - g0) / 864e5);
      let quando = giornoLungo.format(festa.quando);
      if (relativo) quando += ` · ${relativo.format(mancano, 'day')}`;
      metti('festa-nome', x.name);
      metti('festa-quando', quando);
      metti('festa-frase', x.line);
      leva('leva-festa', x);
      /* accanto alla piccola ruota della home: le ore di luce di oggi a 45° nord, contate a mezzogiorno come al
         centro della ruota della pagina */
      if (conf.luceOggi && campo('festa-luce').length) {
        const t = conf.luceOggi;
        const oggi = CIELO.oreDiLuce(new Date(data.getFullYear(), data.getMonth(), data.getDate(), 12));
        const ieri = CIELO.oreDiLuce(new Date(data.getFullYear(), data.getMonth(), data.getDate() - 1, 12));
        const minuti = Math.round(oggi * 60), diff = Math.round((oggi - ieri) * 60);
        const ore = (t.ore || '§h h §m′').replace('§h', Math.floor(minuti / 60)).replace('§m', String(minuti % 60).padStart(2, '0'));
        const quanto = diff === 0 ? t.uguale || '' : ((diff < 0 ? t.meno : t.piu) || '').replace('§', Math.abs(diff));
        metti('festa-luce', `${ore} ${t.di || ''} · ${quanto}`);
      }
      return r;
    }
    const adesso = new Date();
    const l = mostra(adesso);
    const r = mostraFesta(adesso);
    motore.cielo(l, r);
    motore.avvia();
    radice.kgLuna = { mostra, motore, mostraFesta, conf, lingua };
    /* tutto si aggiorna da solo (2026-10-29, l'utente: «i dati e l'animazione deve aggiornarsi in automatico»):
       a ogni battito dell'orologio (sotto) la luna di adesso con la sua fase, la luce, il segno e le prossime
       lune, poi la prossima festa e il sole sulla ruota. Nel cielo della pagina della luna si può scegliere un
       altro giorno: lì la luna la rifà kg-luna-pagina.js. Se cambia una scritta fatta di stelle (il nome della
       fase o della festa, a mezzanotte o passata la festa), le stelle la riscrivono */
    const scritte = () => [...radice.querySelectorAll('[data-kg-scrivi]')].map((el) => el.textContent).join('|');
    radice.kgLuna.aggiorna = (ora = new Date()) => {
      const prima = scritte();
      if (radice.dataset.kgLuna !== 'pagina') motore.cielo(mostra(ora), null);
      const r2 = mostraFesta(ora);
      if (r2) motore.cielo(null, r2);
      if (scritte() !== prima) motore.riscrivi();
      motore.ridisegna();
    };
    radice.dispatchEvent(new CustomEvent('kg-luna:pronta', { bubbles: true }));
  }

  /* il lembo acceso della luna in un disco di raggio r centrato in 50,50: frazione 0 nuova, 0,5 piena */
  function terminatore(frazione, r) {
    const k = Math.cos(2 * Math.PI * frazione);
    const rx = (r * Math.abs(k)).toFixed(2);
    const cresce = frazione < 0.5;
    const lembo = cresce ? 1 : 0;
    const piega = cresce ? (k > 0 ? 0 : 1) : k > 0 ? 1 : 0;
    return `M50 ${50 - r}A${r} ${r} 0 0 ${lembo} 50 ${50 + r}A${rx} ${r} 0 0 ${piega} 50 ${50 - r}Z`;
  }
  radici.forEach(avvia);

  /* l'orologio: la pagina resta in cache e può restare aperta per giorni. Ogni 30 s, tornando sulla scheda e
     tornando dalla cronologia ogni radice si rifà (sopra); poi il documento riceve kg-luna:ora, con l'ora e se
     è cominciato un altro giorno, per le parti della pagina della luna (kg-luna-pagina.js) */
  let giornoVisto = CIELO.inizioGiorno(new Date()).getTime();
  function batti() {
    const ora = new Date();
    const g = CIELO.inizioGiorno(ora).getTime(), nuovoGiorno = g !== giornoVisto;
    giornoVisto = g;
    for (const r of radici) { try { r.kgLuna?.aggiorna?.(ora); } catch (e) { console.error('[kg-luna]', e); } }
    document.dispatchEvent(new CustomEvent('kg-luna:ora', { detail: { ora, nuovoGiorno } }));
  }
  setInterval(batti, 30000);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) batti(); });
  window.addEventListener('pageshow', (e) => { if (e.persisted) batti(); });
  window.KG_LUNA = { terminatore, batti };
})();
